import { useEffect, useRef } from 'react';
import { useCaptionFontsReady } from '../lib/captionFonts';
import {
  captionStateAt,
  layoutSegment,
  drawCaption,
  wordIsBehind,
  activeHookAt,
  captionsHiddenAt,
  drawHookMoment,
  type CaptionSegment,
  type CaptionStyle,
  type CaptionPlacement,
  type TranscriptWord,
  type HookMoment,
  type WordState,
  type Ctx2D,
  type HookScratch,
  type SubjectTrack,
} from '@captionflow/caption-engine';
import { browserMeasurer } from '../engine/measurer';
import { playbackClock } from '../playback/clock';

/** Resolve the style + placement for a given caption line (per-line templates). */
export type StyleResolver = (seg: CaptionSegment) => { style: CaptionStyle; placement: CaptionPlacement };

interface Props {
  words: TranscriptWord[];
  segments: CaptionSegment[];
  style: CaptionStyle;
  placement: CaptionPlacement;
  width: number;
  height: number;
  /** Per-line style override; falls back to `style`/`placement` when absent. */
  styleFor?: StyleResolver;
  /** Hook moments / takeovers to render over the captions. */
  hooks?: HookMoment[];
  /**
   * Which depth layer to draw, for the live "behind the person" preview:
   * - 'all' (default): everything on one canvas (no cut-out active).
   * - 'behind': only content that sits behind the person (hooks flagged behind,
   *   and - when `captionsBehind` - the caption words that duck behind).
   * - 'front': captions + front hooks, drawn ON TOP of the person cut-out.
   */
  layer?: 'all' | 'behind' | 'front';
  /**
   * True when the caption template renders BEHIND the subject. Then the caption
   * words are split across the behind/front layers by the style's `depthLayers`
   * policy (all/emphasized/active), matching the export renderer.
   */
  captionsBehind?: boolean;
  /** The playing video - hooks with a `footage` fill draw it inside their letters. */
  footageRef?: React.RefObject<HTMLVideoElement | null>;
  /** Head track, for hooks that follow the subject. */
  track?: SubjectTrack | null;
}

function indexWords(words: TranscriptWord[]): Map<string, TranscriptWord> {
  const m = new Map<string, TranscriptWord>();
  for (const w of words) m.set(w.id, w);
  return m;
}

/**
 * Renders captions onto a canvas overlay using the SAME drawCaption() the export
 * worker uses - this is the live preview/export parity guarantee. Redraws are
 * driven by the playback clock's rAF loop, not by React re-renders.
 */
export function CaptionCanvas({ words, segments, style, placement, width, height, styleFor, hooks, layer = 'all', captionsBehind = false, footageRef, track }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fontsReady = useCaptionFontsReady();
  const modelRef = useRef({ words, segments, style, placement, styleFor, hooks, layer, captionsBehind, footageRef, track });
  modelRef.current = { words, segments, style, placement, styleFor, hooks, layer, captionsBehind, footageRef, track };
  // Offscreen surfaces for footage-filled hooks, reused across frames.
  const scratchRef = useRef(new Map<string, HookScratch>());
  const scratch = (key: string, w: number, h: number): HookScratch | null => {
    const k = `${key}:${w}x${h}`;
    let sc = scratchRef.current.get(k);
    if (!sc) {
      const cv = document.createElement('canvas');
      cv.width = w;
      cv.height = h;
      const cx = cv.getContext('2d');
      if (!cx) return null;
      sc = { canvas: cv, ctx: cx as unknown as HookScratch['ctx'] };
      scratchRef.current.set(k, sc);
    }
    return sc;
  };

  const drawAt = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, timeMs: number) => {
    const m = modelRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Captions duck out while a hook takeover with hideCaptions is on screen.
    const captionsHidden = captionsHiddenAt(m.hooks, timeMs);
    const state = captionStateAt(m.segments, indexWords(m.words), timeMs);
    // Which caption words draw on THIS layer:
    // - overlay (not behind): all captions on the front/all layer, none behind.
    // - behind: split by depthLayers - behind words on the behind layer, the
    //   rest on the front layer (matches the export's two-pass composite).
    const drawCaptions =
      !captionsHidden &&
      state.segment &&
      state.words.length > 0 &&
      (m.layer === 'all' || m.captionsBehind || m.layer === 'front');
    if (drawCaptions && state.segment) {
      // Resolve style/placement for THIS on-screen line (per-line templates may
      // override the project default), keeping preview identical to the export.
      const resolved = m.styleFor?.(state.segment) ?? { style: m.style, placement: m.placement };
      const layout = layoutSegment(
        state.words,
        resolved.style,
        resolved.placement,
        { width: canvas.width, height: canvas.height },
        browserMeasurer,
      );
      const depthLayers = resolved.style.depthLayers;
      const layerFilter = !m.captionsBehind
        ? undefined
        : m.layer === 'behind'
          ? (ws: WordState, isActive: boolean) => wordIsBehind(ws, isActive, depthLayers)
          : m.layer === 'front'
            ? (ws: WordState, isActive: boolean) => !wordIsBehind(ws, isActive, depthLayers)
            : undefined;
      drawCaption(ctx as unknown as Ctx2D, {
        style: resolved.style,
        layout,
        state,
        measurer: browserMeasurer,
        layerFilter,
      });
    }
    // Hook takeover. A "behind" hook draws on the behind layer (under the
    // person cut-out); a normal hook draws on the front/all layer, on top.
    const hook = activeHookAt(m.hooks, timeMs);
    const hookShows =
      hook && (m.layer === 'all' ? true : m.layer === 'behind' ? Boolean(hook.behind) : !hook.behind);
    if (hook && hookShows) {
      const v = m.footageRef?.current;
      drawHookMoment(ctx as unknown as Ctx2D, {
        hook,
        nowMs: timeMs,
        width: canvas.width,
        height: canvas.height,
        measurer: browserMeasurer,
        // The stage shows the video object-fit: cover, as the engine assumes.
        footage: v && v.readyState >= 2 && v.videoWidth ? { image: v, width: v.videoWidth, height: v.videoHeight } : null,
        scratch,
        track: m.track,
        trackFit: 'cover',
      });
    }
  };

  // Stable rAF subscription (reads latest model via modelRef).
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const unsub = playbackClock.subscribe((t) => drawAt(canvas, ctx, t));
    drawAt(canvas, ctx, playbackClock.timeMs());
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);

  // Redraw when the model changes while paused.
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || playbackClock.isPlaying()) return;
    drawAt(canvas, ctx, playbackClock.timeMs());
  });

  // Canvas text falls back until the webfont is loaded; redraw once fonts arrive
  // (esp. the cute faces that nothing else in the DOM pulls in).
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || playbackClock.isPlaying()) return;
    drawAt(canvas, ctx, playbackClock.timeMs());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontsReady]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={layer === 'all' ? 'caption-canvas' : `caption-canvas cc-${layer}`}
    />
  );
}
